import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Button from '../../components/ui/Button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Privacy Policy - Solis Games</title>
        <meta name="description" content="Privacy Policy for Solis Games - Learn how we collect, use, and protect your personal information." />
      </Helmet>
      
      <Header />
      
      <main className="pt-16 pb-20 md:pb-8">
        <div className="container mx-auto px-4 lg:px-8 py-8 max-w-4xl">
          <div className="bg-card rounded-lg border border-border p-8">
            <h1 className="text-4xl font-bold text-foreground mb-6">Privacy Policy</h1>
            
            <p className="text-muted-foreground mb-8">
              We value your privacy. See what data we collect, how it's stored, and what we use it for.
            </p>
            
            <div className="text-sm text-muted-foreground mb-8">
              <strong>This policy was last updated:</strong> February 26, 2025
            </div>

            <div className="prose prose-invert max-w-none">
              <h2 className="text-2xl font-semibold text-foreground mb-4">Privacy Policy for Solis Games</h2>
              
              <p className="text-foreground mb-6">
                Solis Interactive Inc. ("we," "us," or "Solis Interactive") is committed to protecting your privacy. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you 
                use the Solis Games platform (the "Platform"), play our WebGPU-based games, or visit our website 
                (the "Site"). Please read this carefully. By accessing or using the Platform or Site, you agree to 
                this Privacy Policy.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">1. Information We Collect</h3>
              <p className="text-foreground mb-4">
                We may collect the following types of information when you interact with Solis Games or the Site:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Personal Information:</strong> When you create an account, we may collect your email address, 
                  username, and, if applicable, payment information (processed via third-party providers). You may also 
                  provide additional details like your display name or profile preferences.
                </li>
                <li>
                  <strong>Gameplay Data:</strong> We collect data generated while you play, such as your in-game actions, 
                  game statistics, session duration, and device information (e.g., IP address, browser type, operating system) 
                  to run games via WebGPU and improve your experience.
                </li>
                <li>
                  <strong>Platform Interactions:</strong> If you use chat, voice, or other social features, we may collect 
                  messages or audio data to facilitate communication and enforce our Community Guidelines.
                </li>
                <li>
                  <strong>Analytics Data:</strong> Through Unity (our game engine provider) or other tools, we collect 
                  anonymized data like crash reports, performance metrics, and usage patterns to optimize the Solis Games platform.
                </li>
                <li>
                  <strong>Cookies and Tracking:</strong> We use cookies, web beacons, or similar tools on the Site and 
                  within games (where WebGPU interacts with browser features) to manage logins, track usage, serve content, 
                  or analyze performance. Essential cookies keep games running; others (e.g., for analytics or ads) can be 
                  disabled via your browser settings, though that may impact platform functionality.
                </li>
                <li>
                  <strong>Voluntary Submissions:</strong> If you contact support or submit feedback, we'll collect whatever 
                  you provide (e.g., email, screenshots).
                </li>
              </ul>

              <p className="text-foreground mb-6">
                We don't knowingly collect data from anyone under 13 (or 16 in some regions—see Section 8). If you're a 
                parent and believe your child provided us data, please{' '}
                <Link to="/contact" className="text-primary hover:underline">contact us about privacy matters</Link>.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">2. How We Use Your Information</h3>
              <p className="text-foreground mb-4">We use your data to:</p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>Provide and operate the Solis Games platform, including multiplayer features, leaderboards, and subscription services.</li>
                <li>Process payments or refunds (via third parties).</li>
                <li>Improve our games and platform—e.g., fix bugs, balance mechanics, or optimize WebGPU performance.</li>
                <li>Personalize your experience, like saving your settings or suggesting games.</li>
                <li>Communicate with you about updates, promotions, or support issues.</li>
                <li>Enforce our Terms of Use and protect against cheating or abuse.</li>
                <li>Comply with legal obligations (e.g., GDPR, CCPA).</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">3. How We Share Your Information</h3>
              <p className="text-foreground mb-4">
                We don't sell your personal data. We may share it with:
              </p>
              
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Service Providers:</strong> Companies like Unity (analytics, hosting), payment processors, 
                  or Cloudflare (hosting and security) who help run the Platform. They're bound to protect your data.
                </li>
                <li>
                  <strong>Legal Authorities:</strong> If required by law or to protect our rights (e.g., investigating hacks).
                </li>
                <li>
                  <strong>Business Transfers:</strong> If Solis Interactive is sold or merged, your data may transfer to the new owner.
                </li>
                <li>
                  <strong>Other Players:</strong> Your username, stats, or chat messages may be visible in-game, per the multiplayer experience.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">4. Your Choices and Rights</h3>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li>
                  <strong>Account Deletion:</strong> <Link to="/contact" className="text-primary hover:underline">Contact us about privacy matters</Link> to delete your account. 
                  We'll remove your personal data within 30 days, unless legally required to keep it. Gameplay data 
                  may persist in anonymized form.
                </li>
                <li>
                  <strong>Opt-Out:</strong> You can opt out of promotional emails via the unsubscribe link. Analytics 
                  opt-out depends on your browser settings.
                </li>
                <li>
                  <strong>GDPR (EU/EEA Residents):</strong> You have rights to access, correct, delete, or restrict 
                  your data, or request portability. <Link to="/contact" className="text-primary hover:underline">Contact us about privacy matters</Link>.
                </li>
                <li>
                  <strong>CCPA (California Residents):</strong> You can request what data we collect, ask us to delete it, 
                  or opt out of sales (we don't sell data anyway). No discrimination for exercising rights.
                </li>
                <li>
                  <strong>Other Regions:</strong> Similar rights may apply (e.g., UK GDPR, PIPEDA in Canada). 
                  Reach out if unsure.
                </li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground mb-3">5. Data Retention</h3>
              <p className="text-foreground mb-6">
                We keep personal data as long as your account's active or as needed to provide the Platform. 
                Anonymized gameplay data (e.g., stats for balancing) may be kept indefinitely. Deleted account 
                data is purged within 30 days, except where law requires otherwise.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">6. Security</h3>
              <p className="text-foreground mb-6">
                We use industry-standard measures (e.g., encryption, firewalls) to protect your data. But no online 
                platform is 100% secure—multiplayer features especially carry risks (e.g., chat leaks). Report security 
                concerns using our <Link to="/contact" className="text-primary hover:underline">security issue contact form</Link>.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">7. International Transfers</h3>
              <p className="text-foreground mb-6">
                Solis Interactive is based in the United States in the state of Illinois. If you're outside our region, 
                your data may cross borders. We comply with laws like GDPR for transfers (e.g., Standard Contractual Clauses). 
                <Link to="/contact" className="text-primary hover:underline">Contact us</Link> for details.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">8. Children's Privacy</h3>
              <p className="text-foreground mb-6">
                Solis Games isn't for kids under 13 (or 16 in the EU/EEA). We don't knowingly collect their data. 
                If we find out, we'll delete it ASAP. Parents: <Link to="/contact" className="text-primary hover:underline">contact us about privacy matters</Link> if concerned.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">9. Third-Party Links</h3>
              <p className="text-foreground mb-6">
                The Platform or Site may link to external sites (e.g., Unity servers, payment processors). We only 
                share data that's necessary for gameplay and payment processing.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">10. Changes to This Policy</h3>
              <p className="text-foreground mb-6">
                We may update this as Solis Games evolves. Check back here. Big changes get notified via email or 
                platform notification if required by law.
              </p>

              <h3 className="text-xl font-semibold text-foreground mb-3">11. Contact Us</h3>
              <p className="text-foreground mb-4">Questions? Reach Solis Interactive Inc.:</p>
              <ul className="list-disc pl-6 mb-6 text-foreground space-y-2">
                <li><Link to="/contact" className="text-primary hover:underline">Contact us about privacy matters</Link></li>
                <li>Address: 1440 W Taylor St PMB 4217 Chicago, Illinois 60607, United States</li>
              </ul>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
